import * as THREE from "three";

/** Grid cell the kneaded shape is simplified to for each frame (the clay's edges are 0.035). */
const FRAME_CELL = 0.07;
/** Frames kept; beyond this, every other frame in between is dropped. */
const MAX_FRAMES = 100;

/** One simplified shape of the timelapse, as kept with a piece on the shelf. */
export interface TimelapseFrame {
  /** Strokes counted so far, for the caption. */
  strokes: number;
  positions: Float32Array;
  index: Uint16Array | Uint32Array;
}

/** A frame as the timelapse holds it (and the work in progress keeps it, see work.ts). */
export interface Frame extends TimelapseFrame {
  /** Kneading steps (strokes and resets still in effect) behind this shape. */
  step: number;
}

/**
 * The kneading as a flip book: a simplified copy of the shape after every step, for
 * scrubbing through on the result screen. Undone steps drop out, so it always ends at the
 * current shape.
 */
export class Timelapse {
  private frames: Frame[] = [];
  private shown: { frame: Frame; geometry: THREE.BufferGeometry } | null = null;

  get length(): number {
    return this.frames.length;
  }

  clear(): void {
    this.frames = [];
  }

  /** Steps of the frames held, in order. */
  get steps(): number[] {
    return this.frames.map((f) => f.step);
  }

  /** Takes up frames held before (by the work in progress, see work.ts). */
  load(frames: Frame[]): void {
    this.frames = frames;
  }

  /**
   * Records the shape reached after `step` steps, returning its frame. Frames from `step` on
   * (steps since undone) are replaced.
   */
  record(step: number, strokes: number, positions: Float32Array, index: Uint32Array): Frame {
    const frame = { step, strokes, ...simplify(positions, index, FRAME_CELL) };
    this.frames = this.frames.filter((f) => f.step < step);
    this.frames.push(frame);
    if (this.frames.length > MAX_FRAMES) {
      // Keep the first and the last; drop every other one between.
      const last = this.frames.length - 1;
      this.frames = this.frames.filter((_, k) => k === last || k % 2 === 0);
    }
    return frame;
  }

  /** The frames as they are, to keep (the last is the current shape). */
  snapshot(): TimelapseFrame[] {
    return this.frames.map(({ strokes, positions, index }) => ({ strokes, positions, index }));
  }

  strokesAt(k: number): number {
    return this.frames[k].strokes;
  }

  /** The shape of frame k, as geometry owned by the timelapse (valid until the next call). */
  geometryAt(k: number): THREE.BufferGeometry {
    const frame = this.frames[k];
    if (this.shown?.frame === frame) return this.shown.geometry;
    this.shown?.geometry.dispose();
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(new THREE.BufferAttribute(frame.index, 1));
    geometry.setAttribute("position", new THREE.BufferAttribute(frame.positions, 3));
    geometry.computeVertexNormals();
    this.shown = { frame, geometry };
    return geometry;
  }
}

/**
 * Vertex clustering: vertices in the same grid cell merge into their mean, and triangles
 * that collapse drop out. Crude, but a few milliseconds, and the clay never gets thinner
 * than a couple of cells.
 */
function simplify(
  positions: Float32Array,
  index: Uint32Array,
  cell: number,
): { positions: Float32Array; index: Uint16Array | Uint32Array } {
  const n = positions.length / 3;
  const cellOf = new Int32Array(n);
  const ids = new Map<number, number>();
  const sums: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    // Cells within ±512 of the origin, packed into one number.
    const key = ((Math.floor(x / cell) + 512) * 1024 + Math.floor(y / cell) + 512) * 1024 +
      Math.floor(z / cell) + 512;
    let id = ids.get(key);
    if (id === undefined) {
      id = ids.size;
      ids.set(key, id);
      sums.push(0, 0, 0, 0);
    }
    cellOf[i] = id;
    sums[id * 4] += x;
    sums[id * 4 + 1] += y;
    sums[id * 4 + 2] += z;
    sums[id * 4 + 3]++;
  }
  const out = new Float32Array(ids.size * 3);
  for (let id = 0; id < ids.size; id++) {
    const count = sums[id * 4 + 3];
    out[id * 3] = sums[id * 4] / count;
    out[id * 3 + 1] = sums[id * 4 + 1] / count;
    out[id * 3 + 2] = sums[id * 4 + 2] / count;
  }
  const triangles: number[] = [];
  for (let t = 0; t < index.length; t += 3) {
    const a = cellOf[index[t]], b = cellOf[index[t + 1]], c = cellOf[index[t + 2]];
    if (a !== b && b !== c && c !== a) triangles.push(a, b, c);
  }
  return {
    positions: out,
    index: ids.size < 65536 ? new Uint16Array(triangles) : new Uint32Array(triangles),
  };
}
